import { yupResolver } from "@hookform/resolvers/yup";
import { Tabs } from "antd";
import PaginationCommon from "components/Common/Pagination";
import LoadingFormNormal from "components/shared-components/LoadingFormNormal";
import { useListHubContact } from "hooks/hub-contact/useHubContact";
import { useListHubDelivery } from "hooks/hub-delivery/useHubDelivery";
import { useUpdateHub } from "hooks/hub-management/useHub";
import { useListHubUser } from "hooks/hub-user/useHubUser";
import React, { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import BasicFormSection from "../form/BasicForm.section";
import ContactFormSection from "../form/ContactForm.section";
import DeliveryFormSection from "../form/DeliveryForm.section";
import HubUserFormSection from "../form/HubUserForm.section";
import { validateSchema } from "../form/validate";
import {
  convertMinutesToTime,
  convertTimeToMinutes,
  mapArrayOptionToArrayValue,
  mapArrayValueToArrayOption,
  optionsFixedDayOff,
} from "../utils";
import { ADDRESS_TYPE } from "constants/address";

const { TabPane } = Tabs;

const DetailForm = ({ detail, viewMode }) => {
  const { t } = useTranslation(["hub"]);
  const [keyTab, setKeyTab] = useState("1");
  // const [activeAddressType, setActiveAddressType] = useState("three_level");
  // const is_2_level = activeAddressType === "two_level";

  const hubAddressTypeKey = `hub_address_type_${detail?.id}`;
  const { TWO_LEVEL, THREE_LEVEL } = ADDRESS_TYPE;

  const getInitialAddressType = () => {
    const saved = localStorage.getItem(hubAddressTypeKey);
    if (saved) return saved;
    if (detail?.is_new_address === "N") return TWO_LEVEL;
    if (detail?.is_new_address === "O") return THREE_LEVEL;

    const is_level_2 = !detail?.ward_code || detail?.ward_code === null;
    return is_level_2 ? TWO_LEVEL : THREE_LEVEL;
  };

  const [activeAddressType, setActiveAddressType] = useState(
    getInitialAddressType()
  );

  const handleAddressTypeChange = newType => {
    setActiveAddressType(newType);
    localStorage.setItem(hubAddressTypeKey, newType);
  };
  const [hubUserParams, setHubUserParams] = useState({
    sizePerPage: 10,
    columnFilter: {
      "filter[hub_id]": detail?.id,
      "filter[record_status]": "O",
    },
    expand: "user",
  });

  const mainForm = useForm({
    resolver: yupResolver(validateSchema(t)),
    mode: "all",
    reValidateMode: "onChange",
  });
  useEffect(() => {
    mainForm.clearErrors();
  }, [activeAddressType]);
  const {
    reset,
    getValues,
    trigger,
    formState: { errors, isDirty },
  } = mainForm;

  const is_level_2 = !detail?.ward_code || detail?.ward_code === null;

  useEffect(() => {
    const resetData = {
      ...detail,
      hub_name: detail?.name,
      is_center: detail?.is_center === 1,
      hub_transport_code: detail?.hub_transport_order_code,
      is_level_2,
      ward_code: detail?.ward_code
        ? {
            label: detail?.ward_name,
            value: detail?.ward_code,
          }
        : null,
      district_code: {
        label: detail?.district_name,
        value: detail?.district_code,
      },
      province_code: {
        label: detail?.province_name,
        value: detail?.province_code,
      },
      district_code_new: {
        label: detail?.district_name_new,
        value: detail?.district_code_new,
      },
      province_code_new: {
        label: detail?.province_name_new,
        value: detail?.province_code_new,
      },
      parent_hub_id: detail?.parent_hub_id
        ? {
            label: detail?.parent_hub?.name,
            value: detail?.parent_hub?.id,
          }
        : null,
      fixed_days_off: mapArrayValueToArrayOption(
        detail?.fixed_days_off,
        optionsFixedDayOff
      ),
      forwarding_current_shift: Boolean(detail?.forwarding_current_shift),
      allowed_time: detail?.allowed_time
        ? convertMinutesToTime(detail?.allowed_time)
        : null,
      blood_collection_limit_time: detail?.blood_collection_limit_time
        ? convertMinutesToTime(detail?.blood_collection_limit_time)
        : null,
    };

    reset(resetData);
  }, [detail, reset, is_level_2]);

  const onSuccessUpdateHub = () => {};

  const onErrorUpdateHub = error => {};

  const { isLoading, mutate: updateHub } = useUpdateHub(
    onSuccessUpdateHub,
    onErrorUpdateHub
  );

  const onSubmitBasicForm = () => {
    const fieldsToValidate = [
      "hub_name",
      "hub_order_code",
      "hub_transport_code",
      "address",
      "district_code",
      "province_code",
      "allowed_time",
      "blood_collection_limit_time",
    ];

    if (activeAddressType === THREE_LEVEL) {
      fieldsToValidate.push("ward_code");
    }

    trigger(fieldsToValidate).then(isValid => {
      if (isValid) {
        const data = getValues();

        const fixed_days_off = mapArrayOptionToArrayValue(data.fixed_days_off);

        const payloadData = {
          name: data.hub_name,
          hub_order_code: data.hub_order_code,
          hub_transport_order_code: data.hub_transport_code,
          address: data.address,

          ...(data.ward_code?.value && { ward_code: data.ward_code.value }),
          ...(data.district_code?.value && {
            district_code: data.district_code.value,
          }),
          ...(data.province_code?.value && {
            province_code: data.province_code.value,
          }),

          ...(data.province_code_new?.value && {
            province_code_new: data.province_code_new.value,
          }),
          ...(data.district_code_new?.value && {
            district_code_new: data.district_code_new.value,
          }),
          is_new_address: activeAddressType === TWO_LEVEL ? "N" : "O",

          parent_hub_id: data.parent_hub_id?.value,
          fixed_days_off: fixed_days_off,
          forwarding_current_shift: data.forwarding_current_shift ? 1 : 0,
          allowed_time: data.allowed_time
            ? convertTimeToMinutes(data.allowed_time)
            : undefined,
          blood_collection_limit_time: data.blood_collection_limit_time
            ? convertTimeToMinutes(data.blood_collection_limit_time)
            : undefined,
        };

        updateHub({
          hubId: detail?.id,
          data: payloadData,
        });
      } else {
      }
    });
  };

  const {
    data: delivery,
    refetch: refetchHubDelivery,
    isLoading: isLoadingDelivery,
  } = useListHubDelivery({
    params: {
      queryKey: `hubDeliveries-${detail?.id}`,
      columnFilter: {
        "filter[hub_id]": detail?.id,
      },
      expand: "bus,transport",
    },
  });

  const {
    data: contact,
    refetch: refetchHubContact,
    isLoading: isLoadingContact,
  } = useListHubContact({
    params: {
      columnFilter: {
        "filter[hub_id]": detail?.id,
        "filter[record_status]": "O",
      },
      expand: "bus,transport",
    },
  });

  const {
    data: listHubUser,
    refetch: refetchHubUser,
    isLoading: isLoadingHubUser,
  } = useListHubUser({
    params: hubUserParams,
  });

  const metaDataHubUser = listHubUser?.meta_data;

  useEffect(() => {
    refetchHubDelivery();
    refetchHubContact();
  }, [detail]);

  useEffect(() => {
    refetchHubUser();
  }, [hubUserParams]);

  return (
    <Fragment>
      <Row>
        <Col>
          <form>
            <BasicFormSection
              onSave={onSubmitBasicForm}
              isUpdate={!viewMode}
              viewMode={viewMode}
              mainForm={mainForm}
              activeAddressType={activeAddressType}
              setActiveAddressType={handleAddressTypeChange}
            />
            <Tabs
              defaultActiveKey="1"
              onChange={e => setKeyTab(e)}
              style={{ overflow: "initial" }}
            >
              <TabPane tab={t("title.contact")} key="1">
                {isLoadingContact ? (
                  <LoadingFormNormal
                    countInput="3"
                    countButton="0"
                    formClass="row-cols-3"
                  />
                ) : (
                  <ContactFormSection
                    hubId={detail?.id}
                    contact={contact}
                    refetchHubContact={refetchHubContact}
                    viewMode={viewMode}
                  />
                )}
              </TabPane>
              {!detail?.is_center && (
                <TabPane tab={t("title.delivery")} key="2">
                  {isLoadingDelivery ? (
                    <LoadingFormNormal
                      countInput="3"
                      countButton="0"
                      formClass="row-cols-3"
                    />
                  ) : (
                    <DeliveryFormSection
                      // key={`delivery-${detail?.id}`}
                      hubId={detail?.id}
                      delivery={delivery}
                      refetchHubDelivery={refetchHubDelivery}
                      viewMode={viewMode}
                      activeAddressType={activeAddressType}
                    />
                  )}
                </TabPane>
              )}
              <TabPane tab={t("title.hubUserManagement")} key="3">
                {isLoadingHubUser ? (
                  <LoadingFormNormal
                    countInput="3"
                    countButton="0"
                    formClass="row-cols-3"
                  />
                ) : (
                  <HubUserFormSection
                    listHubUser={listHubUser}
                    refetchHubUser={refetchHubUser}
                    hubId={detail?.id}
                    isCenter={detail?.is_center}
                    viewMode={viewMode}
                  />
                )}
                {metaDataHubUser && Object.keys(metaDataHubUser).length > 0 && (
                  <PaginationCommon
                    setParams={setHubUserParams}
                    params={hubUserParams}
                    metaData={metaDataHubUser}
                  />
                )}
              </TabPane>
            </Tabs>
          </form>
        </Col>
      </Row>
    </Fragment>
  );
};

export default DetailForm;
